/**
 * ActiveFiltersBar — shows active filter groups as dismissible chips.
 *
 * Groups are shown as bordered clusters with their conjunction label.
 * Multiple groups are separated by "and".
 */

import { X } from 'lucide-react';
import type { FilterGroup } from '../hooks/useItemListState';

interface ActiveFiltersBarProps {
  filterGroups: FilterGroup[];
  onRemoveRule: (groupId: string, ruleId: string) => void;
  onRemoveGroup: (groupId: string) => void;
  onClearAll: () => void;
}

export function ActiveFiltersBar({
  filterGroups,
  onRemoveRule,
  onRemoveGroup,
  onClearAll,
}: ActiveFiltersBarProps) {
  const totalRules = filterGroups.reduce((s, g) => s + g.rules.length, 0);
  if (totalRules === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-2 bg-[var(--surface)]/50 border-b border-[var(--rule)]">
      <span className="text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wider shrink-0">
        Filtered by
      </span>

      {filterGroups.map((group, groupIndex) => (
        <div key={group.id} className="flex items-center gap-1.5">
          {/* AND divider between groups */}
          {groupIndex > 0 && (
            <span className="text-[10px] font-bold text-[var(--text-ter)] uppercase tracking-wider px-0.5">
              and
            </span>
          )}

          {/* Group chip */}
          <div className="inline-flex items-center gap-1 bg-[var(--bg-raised)] border border-[var(--slate)] px-1 py-0.5 shadow-sm shadow-indigo-100/30">
            {group.rules.map((rule, ruleIndex) => (
              <div key={rule.id} className="flex items-center gap-0.5">
                {/* Conjunction label between rules */}
                {ruleIndex > 0 && (
                  <span className="text-[9px] font-bold text-[var(--text-ter)] uppercase px-0.5">
                    {group.conjunction}
                  </span>
                )}
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-[var(--slate-dim)] text-[var(--text-sec)] group/rule">
                  <span className="font-semibold">{rule.label}</span>
                  <span className="text-[var(--text-ter)]/80">{rule.displayValue}</span>
                  <button
                    onClick={() => onRemoveRule(group.id, rule.id)}
                    className="ml-0.5 text-[var(--text-sec)] hover:text-[var(--text-pri)] transition-colors"
                    aria-label={`Remove ${rule.label} rule`}
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </span>
              </div>
            ))}

            {/* Remove whole group */}
            {group.rules.length > 1 && (
              <button
                onClick={() => onRemoveGroup(group.id)}
                className="ml-0.5 p-0.5 text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] rounded transition-colors"
                title="Remove group"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
      ))}

      <button
        onClick={onClearAll}
        className="text-[11px] text-[var(--text-ter)] hover:text-[var(--text-sec)] font-medium ml-1 px-1.5 py-0.5 rounded hover:bg-[var(--hover-tint)] transition-colors"
      >
        Clear all
      </button>
    </div>
  );
}
